import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { getCountries, getCountryCallingCode, parsePhoneNumber } from "react-phone-number-input";
import en from "react-phone-number-input/locale/en.json";

// V181 — Admin-only country + dialing-code picker for Create Payment Link.
// Shared <PhoneInputE164 /> (public forms) is intentionally left untouched.

const PINNED = ["IN", "AE", "US", "GB", "CA", "AU", "QA", "SA", "SG"];
const ALIASES = {
    GB: "UK Great Britain Britain England Scotland Wales", US: "USA US America United States of America",
    AE: "UAE Emirates Dubai Abu Dhabi Sharjah", SA: "KSA Saudi", KR: "South Korea", KP: "North Korea",
    NL: "Holland", CI: "Ivory Coast", MM: "Burma", TR: "Turkiye", CZ: "Czechia", MK: "North Macedonia",
    SZ: "Eswatini Swaziland", CV: "Cabo Verde", TL: "East Timor", LK: "Ceylon", NZ: "NZ", HK: "HK", RU: "Russia",
    IR: "Iran", VA: "Vatican", LA: "Laos", PS: "Palestine", BO: "Bolivia", VE: "Venezuela", MD: "Moldova", TZ: "Tanzania",
};
const flagOf = (iso) => iso.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
// Main country for shared calling codes (pinned list covers +1 US, +44 GB, +61 AU …).
const MAIN_FOR_CODE = { 7: "RU", 47: "NO", 212: "MA", 262: "RE", 290: "SH", 358: "FI", 590: "GP", 599: "CW" };

let _codes = null;
const CODES = () => { if (!_codes) _codes = new Set(getCountries().map((i) => getCountryCallingCode(i))); return _codes; };
const mainRank = (c) => { const i = PINNED.indexOf(c.iso); return i >= 0 ? i : MAIN_FOR_CODE[c.code] === c.iso ? 20 : 50; };
function mainCountryFor(code) {
    return PINNED.find((p) => getCountryCallingCode(p) === code) || MAIN_FOR_CODE[code]
        || getCountries().find((i) => getCountryCallingCode(i) === code);
}

let _countries = null;
function countryList() {
    if (_countries) return _countries;
    _countries = getCountries().map((iso) => {
        const code = getCountryCallingCode(iso);
        const name = en[iso] || iso;
        return { iso, code, name, flag: flagOf(iso), hay: `${name} ${iso} +${code} ${code} ${ALIASES[iso] || ""}`.toLowerCase() };
    }).sort((a, b) => a.name.localeCompare(b.name));
    return _countries;
}

export function searchCountries(query) {
    const tokens = (query || "").toLowerCase().trim().split(/\s+/).filter(Boolean);
    const all = countryList();
    if (!tokens.length) return all;
    const words = (c) => c.hay.split(/[^a-z0-9+]+/);
    return all
        .filter((c) => tokens.every((t) => words(c).some((w) => w.startsWith(t))))
        .map((c) => {
            const digits = tokens.map((t) => t.replace(/^\+/, ""));
            const alias = (ALIASES[c.iso] || "").toLowerCase().split(/\s+/);
            const score = digits.includes(c.code) ? 0
                : tokens.includes(c.iso.toLowerCase()) || tokens.some((t) => alias.includes(t)) ? 1
                    : c.name.toLowerCase().startsWith(tokens[0]) ? 2 : 3;
            return [score, c];
        })
        .sort((a, b) => a[0] - b[0] || mainRank(a[1]) - mainRank(b[1]) || a[1].name.localeCompare(b[1].name))
        .map((x) => x[1]);
}

/** Digits typed for `iso` → E.164. Drops a duplicated country code and trunk "0" when that yields a valid number. */
export function toE164(iso, digits) {
    if (!digits) return "";
    const code = getCountryCallingCode(iso);
    const valid = (d) => { try { return !!parsePhoneNumber(d, iso)?.isValid(); } catch { return false; } };
    let d = digits;
    if (d.startsWith(code) && !valid(d) && valid(d.slice(code.length))) d = d.slice(code.length);
    try {
        const p = parsePhoneNumber(d, iso);
        if (p?.isValid() && p.countryCallingCode === code) return p.number;
    } catch { /* fall through */ }
    return `+${code}${d}`;
}

/** Valid under the selected country's dialing plan (+1, +44 … are shared; the picked country is kept). */
export function isValidForCountry(e164, iso) {
    if (!e164) return true;
    try {
        const p = parsePhoneNumber(e164);
        return !!p?.isValid() && p.countryCallingCode === getCountryCallingCode(iso);
    } catch { return false; }
}

function CountryRow({ c, selected, active, onPick, onHover, idx, testid }) {
    return (
        <button type="button" role="option" aria-selected={selected} data-idx={idx} onClick={onPick} onMouseEnter={onHover}
            data-testid={`${testid}-opt-${c.iso}`}
            className={`w-full text-left px-3 py-2.5 flex items-start gap-2.5 text-sm transition-colors ${active ? "bg-blue-50" : "hover:bg-slate-50"} ${selected ? "font-semibold text-[#1E3A8A]" : "text-slate-800"}`}>
            <span className="text-base leading-5 shrink-0" aria-hidden>{c.flag}</span>
            <span className="flex-1 min-w-0 break-words">{c.name} <span className="text-slate-500 tabular-nums">(+{c.code})</span></span>
            {selected && <Check className="w-4 h-4 shrink-0 text-[#1E3A8A] mt-0.5" />}
        </button>
    );
}

function CountryPicker({ iso, onPick, testid }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [active, setActive] = useState(0);
    const rootRef = useRef(null), inputRef = useRef(null), listRef = useRef(null);
    const sel = countryList().find((c) => c.iso === iso);
    const rows = useMemo(() => {
        if (q.trim()) return searchCountries(q);
        const pinned = PINNED.map((p) => countryList().find((c) => c.iso === p)).filter(Boolean);
        return [...pinned, null, ...countryList().filter((c) => !PINNED.includes(c.iso))];
    }, [q]);
    const pickable = rows.filter(Boolean);

    useEffect(() => { setActive(0); }, [q, open]);
    useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 20); }, [open]);
    useEffect(() => {
        if (!open) return undefined;
        const off = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", off);
        document.addEventListener("touchstart", off);
        return () => { document.removeEventListener("mousedown", off); document.removeEventListener("touchstart", off); };
    }, [open]);
    useEffect(() => { listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: "nearest" }); }, [active]);

    const choose = (c) => { onPick(c.iso); setOpen(false); setQ(""); };
    const onKey = (e) => {
        if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(pickable.length - 1, i + 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
        else if (e.key === "Enter") { e.preventDefault(); if (pickable[active]) choose(pickable[active]); }
        else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setOpen(false); }
    };

    let idx = -1;
    return (
        <div ref={rootRef} className="relative">
            <button type="button" onClick={() => setOpen((v) => !v)} aria-haspopup="listbox" aria-expanded={open} data-testid={`${testid}-country`}
                className={`w-full min-h-[44px] flex items-center gap-2 px-3 py-2 text-left text-sm bg-white border rounded-lg transition-colors ${open ? "border-[#1E3A8A] ring-1 ring-[#1E3A8A]" : "border-slate-300 hover:border-slate-400"}`}>
                <span className="text-lg leading-none" aria-hidden>{sel?.flag}</span>
                <span className="flex-1 min-w-0 break-words font-semibold text-[#0F172A]" data-testid={`${testid}-country-selected`}>
                    {sel?.name} <span className="text-slate-500 font-normal tabular-nums">(+{sel?.code})</span>
                </span>
                <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden" data-testid={`${testid}-country-panel`}>
                    <div className="p-2 border-b border-slate-100 flex items-center gap-2">
                        <Search className="w-4 h-4 text-slate-400 shrink-0" />
                        <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} type="text"
                            placeholder="Country or code, e.g. +974" aria-label="Search country or dialing code"
                            data-testid={`${testid}-country-search`} className="w-full h-9 text-base sm:text-sm outline-none bg-transparent placeholder:text-slate-400" />
                    </div>
                    <div ref={listRef} role="listbox" className="max-h-[min(18rem,45vh)] overflow-y-auto overscroll-contain">
                        {!pickable.length && <div className="px-3 py-6 text-center text-xs text-slate-500" data-testid={`${testid}-country-empty`}>No matching country or code</div>}
                        {rows.map((c) => {
                            if (!c) return <div key="divider" className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest font-bold text-slate-400 border-t border-slate-100">All countries</div>;
                            idx += 1;
                            const i = idx;
                            return <CountryRow key={c.iso} c={c} idx={i} testid={testid} selected={c.iso === iso} active={i === active}
                                onPick={() => choose(c)} onHover={() => setActive(i)} />;
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

/** Controlled: `value` is E.164 or "". `onChange(e164, { valid, country })`. */
export default function CountryPhoneInput({ value, onChange, defaultCountry = "IN", testid = "country-phone" }) {
    const initial = useMemo(() => { try { return value ? parsePhoneNumber(value) : null; } catch { return null; } }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const [iso, setIso] = useState(initial?.country || defaultCountry);
    const [text, setText] = useState(initial?.nationalNumber || "");
    const code = getCountryCallingCode(iso);

    const emit = (nextIso, nextText) => {
        const e164 = toE164(nextIso, nextText.replace(/\D/g, ""));
        onChange(e164, { valid: isValidForCountry(e164, nextIso), country: nextIso });
    };
    const onType = (raw) => {
        const t = raw.trim();
        // International format typed or pasted ("+44 7400…" / "0044 7400…"): keep the prefix until a
        // full calling code plus one digit exists, then switch country and keep only the national part.
        if (/^(\+|00)/.test(t)) {
            const digits = t.replace(/^(\+|00)/, "").replace(/\D/g, "");
            const cc = [1, 2, 3].map((n) => digits.slice(0, n)).find((c) => CODES().has(c));
            if (!cc || digits.length <= cc.length) {
                setText(t.startsWith("+") ? `+${digits}` : `00${digits}`);
                onChange(digits ? `+${digits}` : "", { valid: false, country: iso });
                return;
            }
            const rest = digits.slice(cc.length);
            let nextIso = iso;
            if (getCountryCallingCode(iso) !== cc) {
                let parsed = null;
                try { parsed = parsePhoneNumber(`+${digits}`); } catch { /* partial */ }
                nextIso = parsed?.isValid() && parsed.country ? parsed.country : mainCountryFor(cc);
            }
            setIso(nextIso); setText(rest); emit(nextIso, rest);
            return;
        }
        const cleaned = raw.replace(/[^\d\s()-]/g, "");
        setText(cleaned); emit(iso, cleaned);
    };
    const onPickCountry = (nextIso) => { setIso(nextIso); emit(nextIso, text); };

    const valid = isValidForCountry(value, iso);
    const ownerName = (() => { try { const p = value && parsePhoneNumber(value); return p?.isValid() && p.country && p.country !== iso ? en[p.country] : ""; } catch { return ""; } })();

    return (
        <div className="space-y-2" data-testid={testid} data-valid={value ? (valid ? "true" : "false") : "empty"} data-country={iso}>
            <CountryPicker iso={iso} onPick={onPickCountry} testid={testid} />
            <div className="flex">
                <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-slate-300 bg-slate-50 text-sm font-semibold text-slate-700 tabular-nums" data-testid={`${testid}-dial-code`}>
                    +{code}
                </span>
                <input type="tel" inputMode="tel" autoComplete="tel-national" value={text} onChange={(e) => onType(e.target.value)}
                    placeholder="Phone number (optional)" data-testid={`${testid}-number`} aria-label="Phone number without country code"
                    className={`flex-1 min-w-0 px-3 py-2.5 border rounded-r-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6] ${value && !valid ? "border-red-300" : "border-slate-300"}`} />
            </div>
            {value && !valid && (
                <p className="text-[11px] text-red-600" data-testid={`${testid}-error`}>
                    Enter a valid {en[iso] || iso} (+{code}) phone number, or leave it blank.
                </p>
            )}
            {value && valid && ownerName && (
                <p className="text-[11px] text-slate-500" data-testid={`${testid}-shared-code-note`}>
                    +{code} is shared — this number's range is registered to {ownerName}. It will still be sent to +{value.replace(/\D/g, "")}.
                </p>
            )}
        </div>
    );
}
